import { useState } from 'react'
import { Flask } from '@phosphor-icons/react'
import { Banner, DataTable, EmptyState, Field, Input, Select, type Column } from '@/ui'
import { isApiError } from '@/api'
import { istDateKey } from '@/lib/ist'
import { errorMessage } from '../api/core'
import { useOrgs } from '../api/reference'
import { SAMPLE_WHERE_LABEL, useDispatchSample, useSamples, useSites, type SampleRow } from '../api/store'
import { ActionButton } from '../components/Can'
import { FilterBar, SelectFilter } from '../components/FilterBar'
import { FormDialog } from '../components/FormDialog'
import { ConsolePage } from '../components/Layout'
import { Code, Kg, RefLink, Status, When } from '../components/bits'
import { useUrlState } from '../lib/useUrlState'
import {
  SAMPLE_KINDS,
  SAMPLE_STATUSES,
  SAMPLE_STATUS_LABEL,
  canSendToLab,
  dispatchErrorMessage,
  sampleKindLabel,
  toDispatchBody,
  validateDispatch,
  type DispatchForm,
} from '../model/sampleDispatch'

const LIMIT = 25

/** Samples — F5.6-m/n: sealed lot and batch samples, where each one is, and sending them to a lab. */
export function SamplesPage() {
  const [f, setF, { reset, active }] = useUrlState({ kind: '', status: '', siteId: '', page: 1 })
  const sites = useSites()
  const q = useSamples({
    page: f.page,
    limit: LIMIT,
    kind: f.kind || undefined,
    status: f.status || undefined,
    siteId: f.siteId || undefined,
  })
  const [sending, setSending] = useState<SampleRow | null>(null)

  const columns: Column<SampleRow>[] = [
    { id: 'seal', header: 'Seal', cell: (r) => <Code>{r.sealPayload}</Code> },
    { id: 'kind', header: 'Kind', cell: (r) => sampleKindLabel(r.kind), hideBelow: 'lg' },
    {
      id: 'lot',
      header: 'Lot',
      cell: (r) => (r.subjectType === 'UNIT' && r.subjectId ? <RefLink to={`/c/lots/${r.subjectId}`}>{r.unitCode ?? 'Lot'}</RefLink> : '—'),
    },
    {
      id: 'where',
      header: 'Where',
      cell: (r) => (
        <span className="flex flex-col leading-tight">
          <span>{SAMPLE_WHERE_LABEL[r.where] ?? '—'}</span>
          {r.where === 'ON_TRIP' && r.shipmentId ? (
            <RefLink to={`/c/shipments/${r.shipmentId}`} className="text-[12px]">
              Open trip
            </RefLink>
          ) : (
            r.locationPath && <span className="text-[12px] text-muted">{r.locationPath}</span>
          )}
        </span>
      ),
    },
    { id: 'weight', header: 'Weight', align: 'right', cell: (r) => <Kg grams={r.weightG === null ? null : Math.round(r.weightG)} />, hideBelow: 'md' },
    { id: 'collected', header: 'Collected', cell: (r) => <When at={r.collectedAt} dateOnly />, hideBelow: 'md' },
    {
      id: 'lab',
      header: 'Lab',
      hideBelow: 'sm',
      cell: (r) =>
        r.labName ? (
          <span className="flex flex-col leading-tight">
            <span>{r.labName}</span>
            <span className="text-[12px] text-muted">
              <When at={r.sentAt} dateOnly />
              {r.panelRequested && ` · ${r.panelRequested}`}
            </span>
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    { id: 'status', header: 'Status', cell: (r) => <Status status={r.status} label={SAMPLE_STATUS_LABEL[r.status]} /> },
    {
      id: 'act',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      cell: (r) =>
        canSendToLab(r.status) ? (
          <ActionButton
            size="sm"
            variant="secondary"
            perm="samples:dispatch"
            hide
            onClick={(e) => {
              e.stopPropagation()
              setSending(r)
            }}
          >
            Send to lab
          </ActionButton>
        ) : null,
    },
  ]

  return (
    <ConsolePage eyebrow="Store" title="Samples" description="Sealed samples from the gate and from batches: where each one is kept, and when it went to a lab.">
      <DataTable<SampleRow>
        caption="Samples"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter label="Kind" value={f.kind} onChange={(kind) => setF({ kind })} options={SAMPLE_KINDS.map((k) => ({ value: k, label: sampleKindLabel(k) }))} />
            <SelectFilter
              label="Status"
              value={f.status}
              onChange={(status) => setF({ status })}
              options={SAMPLE_STATUSES.map((s) => ({ value: s, label: SAMPLE_STATUS_LABEL[s] ?? s }))}
            />
            <SelectFilter
              label="Site"
              value={f.siteId}
              onChange={(siteId) => setF({ siteId })}
              options={(sites.data ?? []).map((s) => ({ value: s.id, label: s.name || s.code }))}
            />
          </FilterBar>
        }
        empty={<EmptyState icon={<Flask />} title="No samples match" description="A sample shows here once it is sealed at the gate or from a batch." />}
      />
      {sending && <SendToLabDialog sample={sending} onClose={() => setSending(null)} />}
    </ConsolePage>
  )
}

function SendToLabDialog({ sample, onClose }: { sample: SampleRow; onClose: () => void }) {
  const labs = useOrgs('LAB')
  const send = useDispatchSample()
  const today = istDateKey()
  const [form, setForm] = useState<DispatchForm>({ labOrgId: '', panelRequested: '', sentDate: today, dispatchRef: '' })
  const [touched, setTouched] = useState(false)
  const errors = validateDispatch(form, today)
  const show = (k: keyof DispatchForm) => (touched ? errors[k] : undefined)
  const set = (patch: Partial<DispatchForm>) => {
    send.reset()
    setForm((p) => ({ ...p, ...patch }))
  }
  const labOptions = (labs.data ?? []).map((o) => ({ value: o.id, label: o.code ? `${o.name} (${o.code})` : o.name }))

  return (
    <FormDialog
      open
      onClose={onClose}
      title="Send sample to lab"
      description={
        <>
          Seal <Code>{sample.sealPayload}</Code>
          {sample.unitCode && <> · lot {sample.unitCode}</>}
        </>
      }
      saveLabel="Record dispatch"
      loading={send.isPending}
      onSave={async () => {
        setTouched(true)
        if (Object.keys(errors).length > 0) throw new Error('invalid')
        await send.mutateAsync({ id: sample.id, ...toDispatchBody(form, today) })
      }}
    >
      {send.isError && (
        <Banner tone="error" title="Not recorded">
          {dispatchErrorMessage(isApiError(send.error) ? { status: send.error.status, message: errorMessage(send.error) } : { message: errorMessage(send.error) })}
        </Banner>
      )}
      <Field label="Lab" required error={show('labOrgId')} hint={labs.data && labs.data.length === 0 ? 'No lab is registered yet — add one under organisations.' : undefined}>
        <Select<string>
          value={form.labOrgId}
          onChange={(labOrgId) => set({ labOrgId })}
          options={[{ value: '', label: labs.isLoading ? 'Loading labs…' : 'Choose a lab' }, ...labOptions]}
        />
      </Field>
      <Field label="Tests asked for" required error={show('panelRequested')} hint="As written on the lab form, e.g. pesticide residue (full panel).">
        <Input value={form.panelRequested} maxLength={60} onChange={(e) => set({ panelRequested: e.target.value })} />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Sent on" required error={show('sentDate')}>
          <Input type="date" value={form.sentDate} max={today} onChange={(e) => set({ sentDate: e.target.value })} />
        </Field>
        <Field label="Courier or docket no." error={show('dispatchRef')}>
          <Input value={form.dispatchRef} maxLength={40} onChange={(e) => set({ dispatchRef: e.target.value })} />
        </Field>
      </div>
    </FormDialog>
  )
}
